import { useState } from 'react'
import toast from 'react-hot-toast'
import {
    useAdminMaterials,
    useCreateMaterial,
    useUpdateMaterial,
    useDeleteMaterial,
} from '@/api/useAdminCatalog'
import { Spinner } from '@/components/ui'
import type { Material } from '@/api/useMaterials'
import styles from './AdminMaterials.module.css'

interface MaterialForm {
    name: string
    slug: string
    description: string
    is_active: boolean
    sort_order: number
}

const EMPTY_FORM: MaterialForm = {
    name: '',
    slug: '',
    description: '',
    is_active: true,
    sort_order: 0,
}

export function AdminMaterials() {
    const { data: materials, isLoading } = useAdminMaterials()
    const createMaterial = useCreateMaterial()
    const updateMaterial = useUpdateMaterial()
    const deleteMaterial = useDeleteMaterial()

    const [showAddRow, setShowAddRow] = useState(false)
    const [addForm, setAddForm] = useState<MaterialForm>(EMPTY_FORM)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [editForm, setEditForm] = useState<MaterialForm>(EMPTY_FORM)

    const handleAddSave = () => {
        createMaterial.mutate(addForm, {
            onSuccess: () => {
                toast.success('Material creado')
                setShowAddRow(false)
                setAddForm(EMPTY_FORM)
            },
            onError: () => toast.error('Error al crear material'),
        })
    }

    const handleEditStart = (material: Material) => {
        setEditingId(material.id)
        setEditForm({
            name: material.name,
            slug: material.slug,
            description: material.description,
            is_active: material.is_active,
            sort_order: material.sort_order,
        })
    }

    const handleEditSave = (id: number) => {
        updateMaterial.mutate(
            { id, ...editForm },
            {
                onSuccess: () => {
                    toast.success('Material actualizado')
                    setEditingId(null)
                },
                onError: () => toast.error('Error al actualizar material'),
            }
        )
    }

    const handleDelete = (id: number, name: string) => {
        if (!window.confirm(`¿Eliminar el material "${name}"?`)) return
        deleteMaterial.mutate(id, {
            onSuccess: () => toast.success('Material eliminado'),
            onError: () => toast.error('Error al eliminar material'),
        })
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Materiales</h1>
                <button
                    className={styles.addButton}
                    onClick={() => {
                        setShowAddRow(true)
                        setAddForm(EMPTY_FORM)
                        setEditingId(null)
                    }}
                >
                    + Agregar Material
                </button>
            </div>

            {isLoading ? (
                <div className={styles.loading}>
                    <Spinner size="lg" />
                </div>
            ) : (
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Slug</th>
                            <th>Descripción</th>
                            <th>Activo</th>
                            <th>Orden</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {showAddRow && (
                            <tr className={styles.formRow}>
                                <td>
                                    <input
                                        className={styles.formInput}
                                        value={addForm.name}
                                        onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                                        placeholder="Nombre"
                                    />
                                </td>
                                <td>
                                    <input
                                        className={styles.formInput}
                                        value={addForm.slug}
                                        onChange={(e) => setAddForm({ ...addForm, slug: e.target.value })}
                                        placeholder="slug"
                                    />
                                </td>
                                <td>
                                    <input
                                        className={styles.formInput}
                                        value={addForm.description}
                                        onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                                        placeholder="Descripción"
                                    />
                                </td>
                                <td>
                                    <input
                                        type="checkbox"
                                        className={styles.formCheckbox}
                                        checked={addForm.is_active}
                                        onChange={(e) => setAddForm({ ...addForm, is_active: e.target.checked })}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        className={styles.formInput}
                                        value={addForm.sort_order}
                                        onChange={(e) => setAddForm({ ...addForm, sort_order: Number(e.target.value) })}
                                    />
                                </td>
                                <td>
                                    <div className={styles.actions}>
                                        <button
                                            className={styles.saveBtn}
                                            onClick={handleAddSave}
                                            disabled={createMaterial.isPending}
                                        >
                                            Guardar
                                        </button>
                                        <button
                                            className={styles.cancelBtn}
                                            onClick={() => setShowAddRow(false)}
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {!materials || materials.length === 0 ? (
                            <tr>
                                <td colSpan={6} className={styles.empty}>
                                    No hay materiales registrados
                                </td>
                            </tr>
                        ) : (
                            materials.map((material) =>
                                editingId === material.id ? (
                                    <tr key={material.id} className={styles.formRow}>
                                        <td>
                                            <input
                                                className={styles.formInput}
                                                value={editForm.name}
                                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                className={styles.formInput}
                                                value={editForm.slug}
                                                onChange={(e) => setEditForm({ ...editForm, slug: e.target.value })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                className={styles.formInput}
                                                value={editForm.description}
                                                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="checkbox"
                                                className={styles.formCheckbox}
                                                checked={editForm.is_active}
                                                onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                className={styles.formInput}
                                                value={editForm.sort_order}
                                                onChange={(e) => setEditForm({ ...editForm, sort_order: Number(e.target.value) })}
                                            />
                                        </td>
                                        <td>
                                            <div className={styles.actions}>
                                                <button
                                                    className={styles.saveBtn}
                                                    onClick={() => handleEditSave(material.id)}
                                                    disabled={updateMaterial.isPending}
                                                >
                                                    Guardar
                                                </button>
                                                <button
                                                    className={styles.cancelBtn}
                                                    onClick={() => setEditingId(null)}
                                                >
                                                    Cancelar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={material.id}>
                                        <td>{material.name}</td>
                                        <td>{material.slug}</td>
                                        <td>{material.description}</td>
                                        <td>
                                            <span className={`${styles.badge} ${material.is_active ? styles.badgeActive : styles.badgeInactive}`}>
                                                {material.is_active ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td>{material.sort_order}</td>
                                        <td>
                                            <div className={styles.actions}>
                                                <button
                                                    className={styles.editBtn}
                                                    onClick={() => handleEditStart(material)}
                                                >
                                                    Editar
                                                </button>
                                                <button
                                                    className={styles.deleteBtn}
                                                    onClick={() => handleDelete(material.id, material.name)}
                                                    disabled={deleteMaterial.isPending}
                                                >
                                                    Eliminar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            )
                        )}
                    </tbody>
                </table>
            )}
        </div>
    )
}
